import React, { useEffect, useState } from 'react';
import ValidationService from '../../../../../../services/validation';
import GeneralService from '../../../../../../services/general';
import { DefaultButton, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import Label from '../../../../../typography/label';
import PermissionsService from '../../../../../../services/permissions';
import { useStore } from '../../../../../../stores/root';
import ErrorService from '../../../../../../services/general/error';
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import LoadingComponent from '../../../../../feedbacks/loading';
import { IIncomePaymentResourceProps } from '../../../../../../props/finance/incomes/payments/payment';
import IncomePaymentService from '../../../../../../services/finance/incomes/payments/payment';
import Details from '../../../../../typography/details';
import Text from '../../../../../typography/text';
import PaymentMethods from '../../../../../../manifests/paymentMethods';
import IncomePaymentInvoicesList from '../../invoices/general/list';
import moment from 'moment';

interface IIncomePaymentVerifyFormProps {
    paymentId: string;
    onDismissed(refresh?: boolean): void;
}

type FormDataProps = {
    reason: string;
}

type FormDataErrorProps = {
    reason?: string;
}

const IncomePaymentVerifyForm: React.FC<IIncomePaymentVerifyFormProps> = (props: IIncomePaymentVerifyFormProps) => {
    const { banner, user } = useStore();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [payment, setPayment] = React.useState<IIncomePaymentResourceProps | undefined>();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [error, setError] = React.useState<FormDataErrorProps>({});

    const hasPermission = PermissionsService.hasPermission(['incomes.payments.reject'], user.permissions);

    const _onSubmit = async () => {
        setSubmitting(true)
        try {
            await IncomePaymentService.verify(props.paymentId);
            props.onDismissed(true)
        } catch (error) {
            setSubmitting(false)
        }
    }

    useEffect(() => {
        _onRetrievePayment();
    }, []);

    const _onRetrievePayment = async () => {
        try {
            setLoaded(false);

            const _payment = await IncomePaymentService.get(props.paymentId);
            setPayment(_payment);

            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_payment_error',
                text: 'Failed to retrieve payments. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    return <Panel
        headerText={'Verify Payment as Finance'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting && hasPermission ? (
                        <>
                            <PrimaryButton disabled={false} text={"Verify"} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Recording payment ..."} /> : null}
            </Stack>;
        }}
    >
        {hasPermission ? <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <LoadingComponent label={'Retrieving form ...'} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
            {loaded && payment ? <>
                <Text>Are you sure to VERIFY payment with details below?</Text>
                <Details title={'Payment date'} text={moment(payment.paymentDate).format("DD/MM/YYYY")} />
                <Details title={'Method'} text={PaymentMethods.find((pm) => pm.key === payment.method)?.text || 'Cash'} />
                {payment.transferTo ? <Details title={'Transfer to'} text={`${payment.transferTo.accountNumber} a/n ${payment.transferTo.accountName} (${payment.transferTo.bank.name})`} /> : null}
                <Details title={'Amount'} text={`Rp. ${GeneralService.getNumberWithSeparator(Number(payment.amount))}`} />
                <Stack className={'divider'} />
                <Label size={'small'}>Related invoices</Label>
                <IncomePaymentInvoicesList payment={payment}
                    paymentInvoices={payment.invoices}
                    columns={['invoice', 'amount']}
                    hideCommandBar />
            </> : null}
        </Stack> : null}
    </Panel>
};

export default IncomePaymentVerifyForm;
